<script setup lang="ts">
/**
 * PiIcon — typed wrapper for PrimeIcons font icons.
 *
 * Usage:
 *   <PiIcon name="home" />
 *   <PiIcon name="chevron-right" class="text-sm text-primary" />
 *
 * Renders: <i class="pi pi-{name}" />
 * Accepts any extra attrs (class, style, etc.) via inheritAttrs.
 */

export type PiIconName =
  | 'angle-right'
  | 'arrow-down'
  | 'arrow-left'
  | 'arrow-right'
  | 'arrow-up'
  | 'bars'
  | 'bell'
  | 'book'
  | 'bookmark'
  | 'bookmark-fill'
  | 'briefcase'
  | 'building'
  | 'building-columns'
  | 'building-columns'
  | 'calendar'
  | 'calendar-plus'
  | 'calendar-minus'
  | 'chart-bar'
  | 'check'
  | 'check-circle'
  | 'check-square'
  | 'chevron-down'
  | 'chevron-left'
  | 'chevron-right'
  | 'chevron-up'
  | 'circle'
  | 'clock'
  | 'cog'
  | 'comments'
  | 'credit-card'
  | 'download'
  | 'exclamation-circle'
  | 'exclamation-triangle'
  | 'expand'
  | 'eye'
  | 'file'
  | 'paperclip'
  | 'filter'
  | 'folder-open'
  | 'hammer'
  | 'home'
  | 'hourglass'
  | 'id-card'
  | 'images'
  | 'info-circle'
  | 'list-check'
  | 'lock'
  | 'map'
  | 'map-marker'
  | 'minus'
  | 'minus-circle'
  | 'moon'
  | 'percentage'
  | 'phone'
  | 'plus'
  | 'refresh'
  | 'search'
  | 'shield'
  | 'shopping-bag'
  | 'sign-out'
  | 'sliders-h'
  | 'sparkles'
  | 'spinner'
  | 'star-fill'
  | 'sun'
  | 'times'
  | 'user'
  | 'users'
  | 'verified'
  | 'wallet'
  | 'wifi'
  | 'list'
  | 'ban'
  | 'send'
  | 'sort-alt'
  | 'sort-amount-down'
  | 'sort-amount-up'
  | 'tag'
  | 'box'
  | 'arrow-circle-down'
  | 'arrow-circle-up'
  | 'copy'
  | 'check'
  | 'times-circle'
  | 'envelope'
  | 'arrows-h'
  | 'table'
  | 'spin'
  | 'file-pdf'
  | 'ticket'
  | 'inbox'
  | 'envelope'
  | 'headphones'
  | 'phone'
  | 'clock'
  | 'info-circle'
  | 'question-circle'
  | 'upload'
  | 'eye-slash'
  | 'star'
  | 'pencil'
  | 'th-large'
  | 'th-list'
  | 'chart-line'
  | 'car'
  | 'external-link'
  | 'server'
  | 'image'
  | 'image-slash'
  | 'shield-check'
  | 'grid'
  | 'user-plus'
  | 'th-large'
  | 'bolt'
  | 'camera'
  | 'chart-line'
  | 'money-bill'
  | 'ellipsis-v'
  | 'external-link'
  | 'file-edit'
  | 'flag'
  | 'history'
  | 'image'
  | 'link'
  | 'lock-open'
  | 'pencil'
  | 'play'
  | 'power-off'
  | 'replay'
  | 'save'
  | 'sync'
  | 'trash'
  | 'undo'
  | 'window-minimize'
  | 'forward'

export type PiIconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

interface Props {
  name: PiIconName
  /**
   * Icon font-size. Maps to the same scale Tailwind/PrimeFlex use:
   *   xs → 0.75rem (12px) — inline meta accents, status dots
   *   sm → 0.875rem (14px) — table-row glyphs
   *   md → 1rem (16px) — default; matches body text
   *   lg → 1.125rem (18px) — buttons, header icons
   *   xl → 1.25rem (20px) — hero / page-title glyphs
   * Omit to inherit the parent's font-size.
   */
  size?: PiIconSize
}

defineOptions({ inheritAttrs: false })
defineProps<Props>()

const SIZE_REM: Record<PiIconSize, string> = {
  xs: '0.75rem',
  sm: '0.875rem',
  md: '1rem',
  lg: '1.125rem',
  xl: '1.25rem',
}
</script>

<template>
  <i
    :class="`pi pi-${name}`"
    :style="size ? { fontSize: SIZE_REM[size] } : undefined"
    v-bind="$attrs"
  />
</template>
